import { gql } from '@apollo/client'

export const CREATE_USER = gql`
  mutation CREATE_USER(
    $name: String!
    $email: String!
    $password: String!
    $companyName: String!
    $clinicPhones: [String!]!
    $clinicAddress: String!
    $country: String!
    $currency: String!
    $code: String!
    $timeZone: String
    $language: String!
    $referralCode: String!
    $pushNotification: String
    $charts: [ChartTypes]
  ) {
    register(
      name: $name
      email: $email
      password: $password
      companyName: $companyName
      clinicPhones: $clinicPhones
      clinicAddress: $clinicAddress
      country: $country
      currency: $currency
      code: $code
      timeZone: $timeZone
      language: $language
      referralCode: $referralCode
      pushNotification: $pushNotification
      charts: $charts
    )
  }
`

export const LOGOUT = gql`
  mutation LOGOUT($pushNotification: String) {
    logout(pushNotification: $pushNotification)
  }
`

export const LOGOUT_ALL_USERS = gql`
  mutation LOGOUT_ALL_USERS {
    logoutOutAllUsers
  }
`

export const ENABLE_2FA = gql`
  mutation ENABLE_2FA($password: String!) {
    enable2FA(password: $password) {
      secret
      imageUrl
      url
    }
  }
`

export const CONFIRM_2FA_TOKEN = gql`
  mutation CONFIRM_2FA_TOKEN($secret: String!, $token: Int!) {
    confirm2FAToken(secret: $secret, token: $token) {
      id
      twoFactorAuth
    }
  }
`

export const DISABLE_2FA = gql`
  mutation DISABLE_2FA($token: Int!) {
    disable2FA(token: $token) {
      id
      twoFactorAuth
    }
  }
`

export const GENERATE_2FA_CODES = gql`
  mutation GENERATE_2FA_CODES($password: String!) {
    generate2FACodes(password: $password)
  }
`

export const UPDATE_AUTH_MUTATION = gql`
  mutation UPDATE_AUTH_MUTATION($password: String!, $email: String, $newPassword: String, $username: String) {
    updateAuthDetails(password: $password, email: $email, newPassword: $newPassword, username: $username) {
      id
      email
      username
      shouldResetPassword
    }
  }
`

export const UPDATE_USER_MUTATION = gql`
  mutation UPDATE_USER_MUTATION(
    $name: String!
    $phoneNumber: String
    $language: String!
    $isDoctor: Boolean
    $showOverdue: Boolean!
    $doctorPercentage: Float
    $diagnosticFee: Float
    $labOrderPercent: Float
    $diagnosticFeePercent: Boolean
    $dentalNotation: DentalNotationType!
    $alertSettings: AlertSettingsInput!
    $autoAppointmentStatus: [AppointmentStatus!]!
    $automatic: Boolean
    $showAttendance: Boolean
    $requireScreenshot: Boolean
    $automaticExpenses: Boolean
    $expensesFrom: String
    $images: [String]
    $paymentType: PaymentType
    $chart: ChartTypes
    $maxDiscount: Int
    $maxDiscountAnd: Boolean
    $maxDiscountPercent: Int
    $order: Int
    $color: String
    $medicalNumber: String
    $job: String
    $specialityCode: String
    $profiles: [UserProfileInput]
  ) {
    updateUserDetails(
      name: $name
      phoneNumber: $phoneNumber
      language: $language
      isDoctor: $isDoctor
      showOverdue: $showOverdue
      dentalNotation: $dentalNotation
      alertSettings: $alertSettings
      doctorPercentage: $doctorPercentage
      diagnosticFee: $diagnosticFee
      diagnosticFeePercent: $diagnosticFeePercent
      labOrderPercent: $labOrderPercent
      autoAppointmentStatus: $autoAppointmentStatus
      automatic: $automatic
      showAttendance: $showAttendance
      requireScreenshot: $requireScreenshot
      automaticExpenses: $automaticExpenses
      expensesFrom: $expensesFrom
      images: $images
      paymentType: $paymentType
      chart: $chart
      maxDiscount: $maxDiscount
      maxDiscountAnd: $maxDiscountAnd
      maxDiscountPercent: $maxDiscountPercent
      order: $order
      color: $color
      medicalNumber: $medicalNumber
      job: $job
      specialityCode: $specialityCode
      profiles: $profiles
    ) {
      id
      name
      phoneNumber
      language
      isDoctor
      showOverdue
      dentalNotation
      hasValidNumber
      doctorPercentage
      diagnosticFee
      diagnosticFeePercent
      labOrderPercent
      automatic
      showAttendance
      requireScreenshot
      images
      paymentType
      automaticExpenses
      expensesFrom
      chart
      maxDiscount
      maxDiscountAnd
      maxDiscountPercent
      order
      color
      medicalNumber
      job
      specialityCode
      profiles {
        id
        locale
        title
        profession
        bio
        createdAt
        updatedAt
      }
      alertSettings {
        id
        inventory
        masterInventory
        invoice
        reminder
        pendingPatients
        appointmentQrCode
        onlineAppointment
        operations
        orders
        calendar
        treasury
        summaryCron
        appointmentsCron
        newAppointment
        editAppointment
        appointmentStatus
        webNotifications
      }
      autoAppointmentStatus
    }
  }
`

export const CHECK_COMPANY_NAME = gql`
  mutation CHECK_COMPANY_NAME($companyLoginName: String!) {
    checkCompanyNameExists(companyLoginName: $companyLoginName)
  }
`

export const LOGIN_WITH_COMPANY = gql`
  mutation LOGIN_WITH_COMPANY(
    $username: String!
    $password: String!
    $companyLoginName: String!
    $code: String
    $pushNotification: String
    $backup: Boolean
    $add: Boolean
    $rememberMe: Boolean
  ) {
    loginWithCompany(
      username: $username
      password: $password
      companyLoginName: $companyLoginName
      code: $code
      backup: $backup
      pushNotification: $pushNotification
      add: $add
      rememberMe: $rememberMe
    )
  }
`

export const RESET_PASSWORD = gql`
  mutation RESET_PASSWORD($password: String!, $resetPasswordToken: String!) {
    resetPassword(password: $password, resetPasswordToken: $resetPasswordToken)
  }
`

export const SIGN_IN = gql`
  mutation SIGN_IN(
    $email: String!
    $password: String!
    $code: String
    $backup: Boolean
    $pushNotification: String
    $add: Boolean
    $rememberMe: Boolean
  ) {
    login(
      email: $email
      password: $password
      code: $code
      backup: $backup
      pushNotification: $pushNotification
      add: $add
      rememberMe: $rememberMe
    )
  }
`

export const SWITCH_USER = gql`
  mutation SWITCH_USER($id: ID!) {
    switchUser(id: $id)
  }
`

export const SIGN_S3 = gql`
  mutation SIGN_S3($fileName: String!, $fileType: String!, $fileSize: Float!, $token: String) {
    signS3(fileName: $fileName, fileType: $fileType, fileSize: $fileSize, token: $token) {
      signedRequest
      url
    }
  }
`

export const VALIDATE_EMAIL = gql`
  mutation VALIDATE_EMAIL($verificationToken: String!, $email: String) {
    validateEmail(verificationToken: $verificationToken, email: $email)
  }
`

export const RESEND_VALIDATION = gql`
  mutation RESEND_VALIDATION($email: String!) {
    resendValidationEmail(email: $email)
  }
`

export const FORGOT_PASSWORD = gql`
  mutation FORGOT_PASSWORD($email: String!) {
    forgotPassword(email: $email)
  }
`

export const TEST_PERMISSION_GROUP = gql`
  mutation TEST_PERMISSION_GROUP($group: ID) {
    testPermissionGroup(group: $group) {
      id
      permissions
    }
  }
`
